import type { IAssetTemplateModalData } from '../../modal-pick-template/types';
import type { IBaseCampaign, IBaseVariant } from '~/modules/campaign/types';
import type { IAssetTemplateAssigned } from '../types';

import { IAssetTemplateAssignedModalType } from '../../modal-pick-template/types';
import { parseTemplateInfoBy, getLabelByTemplateType } from '~/utils';
import { useAssetTemplateContext } from '~/modules/asset-template';
import { mappingShopifyPageAssigned } from '../helpers';
import { useMemo } from 'react';

export interface IAssetTemplateAssignedResult {
  modalAssignedInfo?: {
    modalTitle: string;
    placeholder?: string;
    primaryActionContent: string;
  };
  variant?: IBaseVariant;
  campaign?: IBaseCampaign;
  templateSuffix?: string;
  pageTypeTxt?: string;
  modalAssignedData?: IAssetTemplateModalData;
  listPageAssigned?: IAssetTemplateAssigned[];
}

export const useInitializeAssetTemplateAssigned = (): IAssetTemplateAssignedResult => {
  const { modalAssetTemplateAssigned, assetsWithAssigned } = useAssetTemplateContext();
  const { modalAssignedType, modalAssignedData } = modalAssetTemplateAssigned;

  const campaign = useMemo(() => modalAssignedData?.campaign, [modalAssignedData?.campaign]);
  const variant = useMemo(() => modalAssignedData?.variant, [modalAssignedData?.variant]);
  const templateSuffix = useMemo(() => variant?.templateSuffix, [variant?.templateSuffix]);
  const templateInfo = useMemo(() => parseTemplateInfoBy({ templateSuffix }), [templateSuffix]);
  const pageTypeTxt = useMemo(
    () => getLabelByTemplateType(templateInfo?.type) || '',
    [templateInfo?.type],
  );

  const listPageAssigned = useMemo(() => {
    return (
      assetsWithAssigned
        ?.find((asset) => asset.key === templateSuffix)
        ?.assigned?.data?.map((item) => mappingShopifyPageAssigned(item))
        ?.filter((item): item is IAssetTemplateAssigned => !!item) || []
    );
  }, [assetsWithAssigned, templateSuffix]);

  const modalAssignedInfo = useMemo(() => {
    if (!modalAssignedType) return undefined;

    const titles: Record<IAssetTemplateAssignedModalType, string> = {
      [IAssetTemplateAssignedModalType.MULTIPLE]: `Select ${pageTypeTxt}s to test`,
      [IAssetTemplateAssignedModalType.VARIANT_B]: `Select ${pageTypeTxt} to test`,
      [IAssetTemplateAssignedModalType.PREVIEW]: `Select ${pageTypeTxt} for Control`,
      [IAssetTemplateAssignedModalType.COPY]: 'Select template for Variant',
    };
    const actionContents: Record<IAssetTemplateAssignedModalType, string> = {
      [IAssetTemplateAssignedModalType.MULTIPLE]: `Confirm`,
      [IAssetTemplateAssignedModalType.VARIANT_B]: `Confirm`,
      [IAssetTemplateAssignedModalType.PREVIEW]: 'Select template',
      [IAssetTemplateAssignedModalType.COPY]: 'Select template',
    };

    const placeholders: Record<IAssetTemplateAssignedModalType, string> = {
      [IAssetTemplateAssignedModalType.MULTIPLE]: `Searching ${pageTypeTxt}s`,
      [IAssetTemplateAssignedModalType.VARIANT_B]: `Searching ${pageTypeTxt}`,
      [IAssetTemplateAssignedModalType.PREVIEW]: `Searching ${pageTypeTxt} for Control`,
      [IAssetTemplateAssignedModalType.COPY]: `Searching ${pageTypeTxt} for Variant`,
    };

    return {
      modalTitle: titles[modalAssignedType],
      placeholder: placeholders[modalAssignedType],
      primaryActionContent: actionContents[modalAssignedType],
    };
  }, [modalAssignedType, pageTypeTxt]);

  return {
    modalAssignedInfo,
    variant,
    campaign,
    templateSuffix,
    pageTypeTxt,
    modalAssignedData,
    listPageAssigned,
  };
};
